import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { INTEGRATIONS_LIST, PageSettingDefinition } from '../constants';
import styles from './IntegrationsPanel.module.css';
import { canUseFacebookAdsSyncIntegrationAndWidget } from '@mm/state/selectors/adAccountsSelecotrs';
import { useSelector } from 'react-redux';
import { selectIsShopifyContext } from '@mm/state/selectors/viewSelectors';
import { HelpCenter } from '../../../../../../common/components/HelpCenter/HelpCenter';
import {
  IntegrationIcon,
  integrationsIconClassNames,
} from '../IntegrationIcon/IntegrationIcon';
import classnames from 'classnames';
import Search from 'antd/es/input/Search';
import { useDebounce } from '@mm/hooks';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useSuppressions } from '../NativeIntegrations/components/ContactSuppressions/use-suppressions';
import { IntegrationTypes } from '@mm/state/model/ConnectionModel';

export interface IntegrationSectionProps {
  section?: PageSettingDefinition;
}

export type IntegrationLinkType =
  | 'native-integrations'
  | 'contact-suppression'
  | 'unsubscribe-list'
  | 'notifications'
  | 'facebook'
  | 'google-ads';

export const IntegrationsPanel = () => {
  const canUseFacebookAds = useSelector(
    canUseFacebookAdsSyncIntegrationAndWidget,
  );
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue.trim(), 500);

  const { invalidateAll } = useConnectionCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  useSuppressions(true);

  const integrationsContainerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation<{ kind: IntegrationTypes }>();
  const locationKindParam = location.state?.kind;
  const params = new URLSearchParams(window.location.search);
  const searchKindParam = params.get('kind');
  const helpLink =
    'https://customers.ai/help/l/en/category/cdvaf3kjxo-crmintegrations';

  const isShopify = useSelector(selectIsShopifyContext);

  const sectionsToRender = useMemo(
    () =>
      INTEGRATIONS_LIST?.filter(v =>
        isShopify ? !['facebook_ads_audience'].includes(v.type) : true,
      ),
    [isShopify],
  );

  useEffect(() => {
    if (locationKindParam || searchKindParam) {
      showSlideoutMenu('nativeIntegrations', {
        type: locationKindParam || searchKindParam,
      });
      if (searchKindParam) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }
    }
    return () => invalidateAll();
  }, [locationKindParam, searchKindParam]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div
          className={styles.integrationsContainer}
          ref={integrationsContainerRef}
        >
          <div>
            <div className={styles.titleContainer}>
              <div className={styles.title}>Integrations</div>

              <HelpCenter helpLink={helpLink} className={styles.help} />
            </div>
            <div>Send leads directly to your CRM</div>
          </div>
          <Search
            placeholder="Search"
            allowClear
            onChange={e => setSearchValue(e.target.value)}
            classNames={{ input: styles.search }}
            style={{ width: 320 }}
          />
          <div className={styles.body}>
            {sectionsToRender
              .filter(section =>
                section.title
                  .toLowerCase()
                  .includes(debouncedSearch.toLowerCase()),
              )
              .map(section => {
                if (section.type === 'facebook' && !canUseFacebookAds) {
                  return <></>;
                }

                return (
                  <div
                    className={styles.section}
                    key={section.type}
                    onClick={() =>
                      showSlideoutMenu('nativeIntegrations', {
                        type: section.type,
                      })
                    }
                  >
                    <div
                      className={classnames(
                        'integrations__header-icon',
                        integrationsIconClassNames[section.type],
                      )}
                    >
                      <IntegrationIcon
                        integrationType={section.type}
                        iconProps={{
                          size: 24,
                          color:
                            section.type === 'mailchimp' ? '#262626' : 'white',
                        }}
                        isActive
                      />
                    </div>

                    <div className={styles.sectionContent}>
                      <div className={styles.sectionTitle}>{section.title}</div>
                      <div className={styles.sectionSubtitle}>
                        {section.subtitle}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};
